/**
 * Unified toast hook wrapping Sonner
 * Provides both object-based and function-based APIs
 */

import { toast as sonnerToast, ExternalToast } from 'sonner'

export interface ToastProps {
  title: string
  description?: string
  variant?: 'default' | 'destructive' | 'success' | 'info' | 'warning'
  duration?: number
}

export function useToast() {
  /**
   * Object-based API for backward compatibility
   * @example
   * toast({ title: 'Success', description: 'Item saved', variant: 'success' })
   */
  const toast = ({ title, description, variant = 'default', duration }: ToastProps) => {
    const options: ExternalToast = { duration }

    switch (variant) {
      case 'destructive':
        return sonnerToast.error(title, {
          description,
          ...options,
        })
      case 'success':
        return sonnerToast.success(title, {
          description,
          ...options,
        })
      case 'info':
        return sonnerToast.info(title, {
          description,
          ...options,
        })
      case 'warning':
        return sonnerToast.warning(title, {
          description,
          ...options,
        })
      default:
        return sonnerToast(title, {
          description,
          ...options,
        })
    }
  }

  /**
   * Function-based API for simple messages
   * @example
   * success('Login successful!')
   */
  const success = (message: string, options?: ExternalToast) => {
    return sonnerToast.success(message, options)
  }

  const error = (message: string, options?: ExternalToast) => {
    return sonnerToast.error(message, options)
  }

  const info = (message: string, options?: ExternalToast) => {
    return sonnerToast.info(message, options)
  }

  const warning = (message: string, options?: ExternalToast) => {
    return sonnerToast.warning(message, options)
  }

  const loading = (message: string, options?: ExternalToast) => {
    return sonnerToast.loading(message, options)
  }

  /**
   * Promise-based toast for async operations
   * @example
   * promise(
   *   fetchData(),
   *   {
   *     loading: 'Saving...',
   *     success: 'Saved successfully!',
   *     error: 'Failed to save'
   *   }
   * )
   */
  const promise = <T>(
    promiseOrFn: Promise<T> | (() => Promise<T>),
    messages: {
      loading: string
      success: string | ((data: T) => string)
      error: string | ((error: unknown) => string)
    },
    options?: ExternalToast
  ) => {
    return sonnerToast.promise(promiseOrFn, { ...messages, ...options })
  }

  return {
    toast,
    success,
    error,
    info,
    warning,
    loading,
    promise,
  }
}
